/**
 * QR code detail: image preview, image download and public call link.
 * Images are generated by the backend and served from its /qr_codes
 * static path; the public link triggers the scan endpoint.
 */

import {
  Show,
  SimpleShowLayout,
  TextField,
  FunctionField,
  useRecordContext,
  useTranslate,
} from 'react-admin';
import { useState } from 'react';
import { Box, Button, Link, Typography } from '@mui/material';
import { FileDownloadOutlined as DownloadIcon } from '@mui/icons-material';
import { absoluteUrl } from '@/utils/url';

const QR_IMAGES_URL = import.meta.env.VITE_QR_IMAGES_URL || 'http://localhost:5001/qr_codes';
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api/v1';

const QrPreview = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  const [downloading, setDownloading] = useState(false);
  if (!record) return null;

  // The download attribute is ignored cross-origin; fetch and save as blob
  const handleDownload = async (imageUrl: string) => {
    setDownloading(true);
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${record.qr_name}.jpg`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  const hasImage = record.qr_img && record.qr_img !== 'none.jpg';
  const imageUrl = hasImage ? `${QR_IMAGES_URL}/${record.qr_img}` : null;
  const publicUrl = record.calllink_id ? absoluteUrl(`${API_URL}/qrcodes/scan/${record.calllink_id}`) : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
      {imageUrl ? (
        <>
          <img
            src={imageUrl}
            alt={record.qr_name}
            style={{ width: 220, height: 220, objectFit: 'contain' }}
          />
          <Button
            onClick={() => handleDownload(imageUrl)}
            disabled={downloading}
            startIcon={<DownloadIcon />}
            variant="outlined"
            sx={{ width: 'fit-content' }}
          >
            {translate('resources.qrcodes.actions.download')}
          </Button>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {translate('resources.qrcodes.no_image')}
        </Typography>
      )}
      {publicUrl && (
        <Typography variant="body2">
          {translate('resources.qrcodes.fields.public_link')}:{' '}
          <Link href={publicUrl} target="_blank" rel="noopener">
            {publicUrl}
          </Link>
        </Typography>
      )}
    </Box>
  );
};

export const QrCodeShow = () => {
  const translate = useTranslate();
  return (
    <Show>
      <SimpleShowLayout>
        <QrPreview />
        <TextField source="qr_name" />
        <FunctionField
          source="type"
          render={(record: any) => translate(`resources.qrcodes.types.${record?.type}`, { _: record?.type })}
        />
        <FunctionField
          source="status"
          render={(record: any) =>
            record?.status === 'active'
              ? translate('common.status.active', { _: 'Activo' })
              : translate('common.status.inactive', { _: 'Inactivo' })
          }
        />
        <TextField source="default_staff_name" />
        <FunctionField
          source="department_id"
          label={translate('resources.qrcodes.fields.department_id')}
          render={(record: any) =>
            Array.isArray(record?.deptArr) ? record.deptArr.join(', ') : ''
          }
        />
        <FunctionField
          source="staffs"
          label={translate('resources.qrcodes.fields.staffs')}
          render={(record: any) =>
            Array.isArray(record?.staffArr) ? record.staffArr.join(', ') : ''
          }
        />
      </SimpleShowLayout>
    </Show>
  );
};

export default QrCodeShow;
